import { CSSProperties, FC, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import classNames from 'classnames';
import { Hint } from '@redoc/ui-kit';
import { Button } from 'src/Components';
import Input from '../Input/Input';
import { useOutsideClick } from 'src/Hooks';
import { ReactComponent as CloseIcon } from './assets/close small.svg';
import { ReactComponent as TickIcon } from './assets/tick small.svg';
import { IModalEditProps } from '.';
import st from './ModalEdit.module.scss';

export const ModalEdit: FC<IModalEditProps> = (props) => {
	const {
		type = 'text',
		initValue = '',
		closeOutside = false,
		openUp = false,
		onChangeValue,
		renderInput,
		hint,
		className,
		children,
		...otherProps
	} = props;
	const wrapperRef = useRef<HTMLDivElement>(null);
	const modalRef = useOutsideClick<HTMLDivElement>(() => {
		return closeOutside && setEditMode(false);
	});

	const [editMode, setEditMode] = useState<boolean>(false);
	const [value, setValue] = useState<string>(initValue);
	const [position, setPosition] = useState<CSSProperties | undefined>(
		undefined,
	);

	useLayoutEffect(() => {
		/** Минимальный размер отсутпа от кнопки открытия */
		const padding = 4;

		const calcOffsetModalPosition = () => {
			const wrapper = wrapperRef.current;
			const modal = modalRef.current;

			if (!wrapper) {
				return;
			}

			const wrapperRect = wrapper.getBoundingClientRect();
			const modalWidth = modal?.offsetWidth ?? 0;
			const modalHeight = modal?.offsetHeight ?? 0;
			const windowWidth = window.innerWidth;
			const windowHeight = window.innerHeight;

			let left = wrapperRect.right + padding;

			/** Модалка выходит за правый край окна */
			const overflowsRight =
				modalWidth && left + modalWidth > windowWidth - padding;

			if (overflowsRight) {
				left = Math.max(windowWidth - modalWidth - padding, padding);
			}

			/** Максимально допустимая граница модалки по вертикали */
			const maxEdge = windowHeight - modalHeight - padding;

			if (openUp) {
				let bottom = windowHeight - wrapperRect.bottom;

				/** Модалка выходит за верхний край окна */
				const overflowsTop =
					modalHeight &&
					windowHeight - bottom - modalHeight < padding;

				if (overflowsTop) {
					bottom = Math.max(maxEdge, padding);
				}

				setPosition({ bottom, left });
			} else {
				let top = wrapperRect.bottom + padding;

				/** Модалка выходит за нижний край окна */
				const overflowsBottom =
					modalHeight && top + modalHeight > windowHeight - padding;

				if (overflowsBottom) {
					top = Math.max(maxEdge, padding);
				}

				setPosition({ top, left });
			}
		};

		const scrollContainer = window.document.querySelector('.scrolled');

		if (scrollContainer) {
			scrollContainer.addEventListener('scroll', calcOffsetModalPosition);
		}

		// Контент renderInput может
		// поменять размер модалки асинхронно после открытия,
		// поэтому необходимо пересчитывать позицию при ресайзе,
		// чтобы она не уехала за пределы экрана
		const modal = modalRef.current;

		const resizeObserver =
			modal && window.ResizeObserver
				? new ResizeObserver(calcOffsetModalPosition)
				: undefined;

		resizeObserver?.observe(modal!);

		calcOffsetModalPosition();

		return () => {
			if (scrollContainer) {
				scrollContainer.removeEventListener(
					'scroll',
					calcOffsetModalPosition,
				);
			}
			resizeObserver?.disconnect();
		};
		// eslint-disable-next-line
	}, [editMode]);

	const renderModal = () => {
		if (renderInput) {
			return (
				<div
					ref={modalRef}
					className={st.modalEditCustom}
					style={{ ...position }}
				>
					{renderInput({
						...props,
						closeModal: () => {
							return setEditMode(false);
						},
					})}
				</div>
			);
		}
		return (
			<div
				ref={modalRef}
				className={st.modalEdit}
				style={{ ...position }}
			>
				<div className="emptyIcon" />
				<Input
					type={type}
					value={value}
					onChangeValue={(v) => {
						return setValue(v);
					}}
				/>
				<div className={st.toolbar}>
					<Button
						size="min"
						outline
						onClick={() => {
							return !!onChangeValue && onChangeValue(value);
						}}
						disable={initValue === value}
					>
						<TickIcon />
					</Button>
					<Button
						size="min"
						outline
						onClick={() => {
							return setEditMode(false);
						}}
					>
						<CloseIcon />
					</Button>
				</div>
			</div>
		);
	};

	const trigger = (
		<Button
			size="min"
			outline
			onClick={() => {
				return setEditMode(true);
			}}
			active={editMode}
		>
			{children}
		</Button>
	);

	return (
		<div
			ref={wrapperRef}
			className={classNames(st.modalEditWrapper, className, {
				[st.active]: editMode,
			})}
			{...otherProps}
		>
			{hint ? (
				<Hint messageText={hint} placement="top" disabled={editMode}>
					{trigger}
				</Hint>
			) : (
				trigger
			)}
			{editMode && createPortal(renderModal(), window.document.body)}
		</div>
	);
};
